import { Box, useTheme } from '@mui/material';
import { logError } from '@ptek/client-logger';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { recorderStyles as styles } from '../styles/recorderStyles';

const VISUALIZER_CONFIG = {
  updateInterval: 30,
  barHeight: {
    min: 15,
    max: 90,
  },
  sensitivity: 20.0,
  smoothing: 0.5,
  pulseSpeed: 2000,
  idleMinVolume: 0.02,
} as const;

interface WaveformVisualizerProps {
  audioStream?: MediaStream | null;
  barCount?: number;
  isTranscribing?: boolean;
}

const createCenterOutPattern = (
  barCount: number,
  intensity: number
): number[] => {
  const bars: number[] = [];
  const center = (barCount - 1) / 2;

  for (let i = 0; i < barCount; i++) {
    const distanceFromCenter = Math.abs(i - center) / center;

    const baseWeight = Math.max(0.2, 1 - distanceFromCenter * 0.8);
    const audioBoost = intensity > 0.3 ? intensity * 2 : 1;
    const centerWeight =
      i === Math.floor(center) || i === Math.ceil(center)
        ? baseWeight * audioBoost
        : baseWeight;

    const positionVariation = 0.85 + Math.sin(i * 0.8) * 0.15;
    const finalWeight = centerWeight * positionVariation;

    const height =
      VISUALIZER_CONFIG.barHeight.min +
      intensity *
        finalWeight *
        (VISUALIZER_CONFIG.barHeight.max - VISUALIZER_CONFIG.barHeight.min);
    bars.push(Math.min(VISUALIZER_CONFIG.barHeight.max, height));
  }

  return bars;
};

const createStaticPattern = (barCount: number): number[] => {
  return Array(barCount).fill(VISUALIZER_CONFIG.barHeight.min);
};

export const WaveformVisualizer: FC<WaveformVisualizerProps> = ({
  audioStream,
  barCount = 12,
  isTranscribing = false,
}) => {
  const theme = useTheme();
  const [bars, setBars] = useState<number[]>(() =>
    createStaticPattern(barCount)
  );

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const smoothedVolumeRef = useRef(0);
  const startTimeRef = useRef(Date.now());
  // If we created our own microphone stream (fallback), retain it so we can stop tracks on cleanup
  const fallbackStreamRef = useRef<MediaStream | null>(null);
  // Reuse buffer for analyser to avoid per-tick allocations
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);

  const setupAudioAnalysis = useCallback(async (stream: MediaStream) => {
    try {
      if (
        !audioContextRef.current ||
        audioContextRef.current.state === 'closed'
      ) {
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }

      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.05;
      analyserRef.current.minDecibels = -70;
      analyserRef.current.maxDecibels = -5;

      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      return true;
    } catch (error) {
      logError('WaveformVisualizer', 'Audio setup failed:', error);
      return false;
    }
  }, []);

  const getAudioVolume = useCallback((): number => {
    if (!analyserRef.current) {
      return 0;
    }

    const binCount = analyserRef.current.frequencyBinCount;
    if (!dataArrayRef.current || dataArrayRef.current.length !== binCount) {
      dataArrayRef.current = new Uint8Array(binCount);
    }
    const dataArray = dataArrayRef.current;
    analyserRef.current.getByteFrequencyData(dataArray);

    let sum = 0;
    const relevantBins = Math.min(dataArray.length, 30);

    for (let i = 0; i < relevantBins; i++) {
      sum += dataArray[i] * dataArray[i]; // Square values for better sensitivity
    }

    const rms = Math.sqrt(sum / relevantBins);
    const normalizedVolume = Math.min(
      1,
      (rms / 255) * VISUALIZER_CONFIG.sensitivity
    );

    smoothedVolumeRef.current =
      smoothedVolumeRef.current * VISUALIZER_CONFIG.smoothing +
      normalizedVolume * (1 - VISUALIZER_CONFIG.smoothing);

    return smoothedVolumeRef.current;
  }, []);

  const updateVisualization = useCallback(() => {
    const volume = getAudioVolume();
    const enhancedVolume = Math.max(VISUALIZER_CONFIG.idleMinVolume, volume);
    setBars(createCenterOutPattern(barCount, enhancedVolume));
  }, [barCount, getAudioVolume]);

  const startVisualization = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    startTimeRef.current = Date.now();
    intervalRef.current = setInterval(
      updateVisualization,
      VISUALIZER_CONFIG.updateInterval
    );
  }, [updateVisualization]);

  const stopVisualization = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    stopVisualization();

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    smoothedVolumeRef.current = 0;
    dataArrayRef.current = null;

    if (fallbackStreamRef.current) {
      try {
        fallbackStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch (error) {
        logError('WaveformVisualizer', 'Failed to stop fallback tracks', error);
      }
      fallbackStreamRef.current = null;
    }
  }, [stopVisualization]);

  useEffect(() => {
    const initializeVisualization = async () => {
      cleanup();

      let streamToUse = audioStream;

      if (!streamToUse) {
        try {
          streamToUse = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          // Retain reference so we can stop tracks on cleanup
          fallbackStreamRef.current = streamToUse;
        } catch (error) {
          logError(
            'WaveformVisualizer',
            'Could not access microphone for visualization:',
            error
          );
        }
      }

      if (streamToUse) {
        const success = await setupAudioAnalysis(streamToUse);
        if (!success) {
          setBars(createStaticPattern(barCount));
          return;
        }
      }

      startVisualization();
    };

    initializeVisualization();

    return cleanup;
  }, [audioStream, barCount, setupAudioAnalysis, startVisualization, cleanup]);

  useEffect(() => {
    setBars(createStaticPattern(barCount));
  }, [barCount]);

  const waveformBarStyle = useMemo(
    () => styles.getWaveformBar(theme, isTranscribing),
    [theme, isTranscribing]
  );

  return (
    <Box sx={styles.waveformVisualizer}>
      {bars.map((height, index) => (
        <Box
          key={index}
          sx={waveformBarStyle}
          // Use inline style for highly dynamic values to avoid Emotion/MUI injecting new CSS rules per tick
          style={{ height: `${height}%` }}
        />
      ))}
    </Box>
  );
};
